import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Button, Container, Typography } from "@mui/material";
import { Android } from "@mui/icons-material";
import JsonLd from "../components/JsonLd";
import BlogCover from "../components/blog/BlogCover";
import BlogPostCard from "../components/blog/BlogPostCard";
import { getPostBySlug, getRelatedPosts } from "../content/blog";
import { PLAY_STORE_URL } from "../config/store";
import { blogPostingJsonLd, faqPageJsonLd } from "../utils/schema";
import {
  formatBlogDate,
  formatBlogTag,
  getPostCategory,
  getPostUpdated,
  getReadingLabel,
} from "../utils/blogFormat";

const chipSx = {
  display: "inline-flex",
  alignItems: "center",
  px: 1.25,
  py: 0.4,
  borderRadius: 999,
  bgcolor: "rgba(94,53,177,0.1)",
  color: "primary.main",
  fontWeight: 700,
  fontSize: "0.72rem",
  letterSpacing: "0.04em",
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const related = post ? getRelatedPosts(post.slug, 3) : [];

  if (!post) {
    return (
      <Box sx={{ bgcolor: "#f7f5fb", py: 10, px: 2 }}>
        <Container maxWidth="sm" sx={{ textAlign: "center" }}>
          <Typography component="h1" fontWeight={800} sx={{ mb: 1.5, fontSize: "1.8rem" }}>
            Post not found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            That guide is not on the Treasure blog.
          </Typography>
          <Button component={RouterLink} to="/blog" variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>
            Back to blog
          </Button>
        </Container>
      </Box>
    );
  }

  const category = getPostCategory(post);
  const updated = getPostUpdated(post);
  const headings = (post.headings || []).filter((item) => item.depth === 2);
  const faqs = post.faqs || [];
  const graph = [blogPostingJsonLd(post), faqPageJsonLd(post)].filter(Boolean);

  return (
    <Box sx={{ bgcolor: "#f7f5fb", pb: 8 }}>
      <JsonLd data={{ "@context": "https://schema.org", "@graph": graph }} id="jsonld-blog" />
      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 3 }, px: 2 }}>
        <Typography
          component="nav"
          aria-label="Breadcrumb"
          sx={{ mb: 2, fontSize: "0.85rem", color: "text.secondary", fontWeight: 600 }}
        >
          <Box component={RouterLink} to="/" sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
            Home
          </Box>
          {" · "}
          <Box component={RouterLink} to="/blog" sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
            Blog
          </Box>
          {" · "}
          <Box component="span">{post.title}</Box>
        </Typography>

        <Box
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "rgba(94,53,177,0.12)",
            boxShadow: "0 16px 40px rgba(94,53,177,0.12)",
          }}
        >
          <BlogCover post={post} size="hero" priority alt={post.title} titleComponent="p" />
        </Box>

        <Box
          component="article"
          sx={{
            mt: { xs: 2.5, md: 3 },
            mx: "auto",
            maxWidth: 760,
            p: { xs: 2.5, md: 5 },
            bgcolor: "white",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "rgba(94,53,177,0.12)",
            boxShadow: "0 8px 24px rgba(94,53,177,0.06)",
            "& h2": {
              fontWeight: 800,
              mt: 4.5,
              mb: 1.5,
              fontSize: { xs: "1.25rem", md: "1.45rem" },
              letterSpacing: "-0.02em",
              color: "text.primary",
              scrollMarginTop: 96,
            },
            "& h3": {
              fontWeight: 800,
              mt: 3,
              mb: 1,
              fontSize: { xs: "1.05rem", md: "1.15rem" },
              color: "text.primary",
            },
            "& p": { mb: 2.25, lineHeight: 1.85, color: "text.secondary", fontSize: { xs: "1.02rem", md: "1.08rem" } },
            "& ul, & ol": { mb: 2.25, pl: 3, color: "text.secondary", lineHeight: 1.85, fontSize: { xs: "1.02rem", md: "1.08rem" } },
            "& li": { mb: 0.85 },
            "& a": { color: "primary.main", fontWeight: 600 },
            "& strong": { color: "text.primary" },
            "& figure.blog-figure": {
              my: 4,
              mx: "auto",
              maxWidth: 300,
              textAlign: "center",
            },
            "& figure.blog-figure .blog-device": {
              padding: "8px",
              bgcolor: "#F3F0F8",
              border: "1px solid #E4DFF0",
              borderRadius: "22px",
              boxShadow: "0 22px 40px rgba(69,39,160,0.16)",
            },
            "& figure.blog-figure picture": { display: "block" },
            "& figure.blog-figure img": {
              width: "100%",
              height: "auto",
              borderRadius: "16px",
              border: "none",
              boxShadow: "none",
              display: "block",
            },
            "& figure.blog-figure figcaption": {
              mt: 1.5,
              fontSize: "0.88rem",
              color: "text.secondary",
              lineHeight: 1.5,
              fontWeight: 500,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <Box component="span" sx={chipSx}>
              {category}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              <time dateTime={post.date}>{formatBlogDate(post.date)}</time>
              {updated && updated !== post.date ? (
                <>
                  {" · Updated "}
                  <time dateTime={updated}>{formatBlogDate(updated)}</time>
                </>
              ) : null}
              {" · "}
              {getReadingLabel(post)}
            </Typography>
          </Box>

          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.7rem", md: "2.25rem" },
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              mb: 1.5,
              color: "text.primary",
            }}
          >
            {post.title}
          </Typography>

          {post.description && (
            <Typography
              sx={{
                mb: 2,
                fontSize: { xs: "1.05rem", md: "1.12rem" },
                lineHeight: 1.7,
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              {post.description}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontWeight: 600 }}>
            By Weps Technology
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 3.5 }}>
            {(post.tags || []).map((tag) => (
              <Box key={tag} component="span" sx={chipSx}>
                {formatBlogTag(tag)}
              </Box>
            ))}
          </Box>

          {headings.length >= 3 && (
            <Box
              component="nav"
              aria-label="On this page"
              sx={{
                mb: 4,
                p: 2,
                borderRadius: 2,
                bgcolor: "#f7f5fb",
                border: "1px solid",
                borderColor: "rgba(94,53,177,0.12)",
              }}
            >
              <Typography fontWeight={800} sx={{ mb: 1, fontSize: "0.92rem" }}>
                On this page
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 2.5, color: "text.secondary" }}>
                {headings.map((item) => (
                  <Box component="li" key={item.id} sx={{ mb: 0.6 }}>
                    <Box
                      component="a"
                      href={`#${item.id}`}
                      sx={{ color: "primary.main", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                    >
                      {item.text}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box dangerouslySetInnerHTML={{ __html: post.html }} />

          {faqs.length > 0 && (
            <Box component="section" aria-labelledby="faq-heading" sx={{ mt: 2 }}>
              <Typography id="faq-heading" component="h2" sx={{ fontWeight: 800, mt: 4.5, mb: 1.5, fontSize: { xs: "1.25rem", md: "1.45rem" } }}>
                Frequently asked questions
              </Typography>
              {faqs.map((item) => (
                <Box key={item.question} sx={{ mb: 2.5 }}>
                  <Typography component="h3" sx={{ fontWeight: 800, mb: 0.75, fontSize: "1.05rem", color: "text.primary" }}>
                    {item.question}
                  </Typography>
                  <Typography sx={{ color: "text.secondary", lineHeight: 1.8 }}>{item.answer}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mt: 3,
            mx: "auto",
            maxWidth: 760,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            bgcolor: "white",
            border: "1px solid",
            borderColor: "rgba(94,53,177,0.12)",
            textAlign: "center",
            boxShadow: "0 8px 24px rgba(94,53,177,0.06)",
          }}
        >
          <Typography fontWeight={800} sx={{ mb: 1, fontSize: "1.2rem" }}>
            Track this on Android
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7, maxWidth: 440, mx: "auto" }}>
            Record income and expenses in Treasure and see where your money goes each month.
          </Typography>
          <Button
            component="a"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<Android />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
          >
            Get Treasure on Google Play
          </Button>
        </Box>

        {related.length > 0 && (
          <Box sx={{ mt: { xs: 5, md: 6 } }}>
            <Typography fontWeight={800} sx={{ mb: 2.5, fontSize: "1.35rem", letterSpacing: "-0.02em" }}>
              Related articles
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                gap: 2.5,
              }}
            >
              {related.map((item) => (
                <BlogPostCard key={item.slug} post={item} variant="related" />
              ))}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default BlogPost;
